import { useMemo } from 'react';
import { cn } from '@/lib/utils';

export type OficinaMercosulPlateProps = {
  placa: string | null | undefined;
  className?: string;
  /** Reduz largura e tipografia para colunas estreitas (ex.: Painel v2). */
  compact?: boolean;
};

/** Placa estilo Mercosul em duas linhas (faixa BRASIL + corpo branco), como no padrão quadrado/moto. */
export function OficinaMercosulPlate({ placa, className, compact }: OficinaMercosulPlateProps) {
  const { line1, line2, label } = useMemo(() => {
    const raw = (placa ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!raw) {
      return { line1: '—', line2: '—', label: 'Placa indisponível' };
    }
    if (raw.length <= 3) {
      return { line1: raw, line2: '', label: placa ?? raw };
    }
    return {
      line1: raw.slice(0, 3),
      line2: raw.slice(3),
      label: placa ?? raw,
    };
  }, [placa]);

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden rounded-lg border border-neutral-300 bg-white shadow-sm',
        'dark:border-neutral-500 dark:bg-neutral-50',
        compact ? 'w-[4.75rem]' : 'w-[7.5rem]',
        className
      )}
      role="img"
      aria-label={`Placa ${label}`}
    >
      <div
        className={cn(
          'bg-[#002776] text-center font-bold uppercase leading-none tracking-wide text-white',
          compact ? 'py-[4px] text-[10px]' : 'py-[6px] text-[16px]'
        )}
      >
        BRASIL
      </div>
      <div className={cn('flex flex-col items-center justify-center px-0.5 text-center', compact ? 'py-1.5' : 'px-1 py-3')}>
        <span
          className={cn(
            'block font-black leading-none text-black [font-family:system-ui,-apple-system,sans-serif] tracking-tight',
            compact ? 'text-[15px]' : 'text-[26px]'
          )}
        >
          {line1}
        </span>
        <span
          className={cn(
            'mt-0.5 block min-h-[0.85em] font-black leading-none text-black [font-family:system-ui,-apple-system,sans-serif] tracking-tight',
            compact ? 'text-[15px]' : 'text-[26px]'
          )}
        >
          {line2 || '\u00A0'}
        </span>
      </div>
    </div>
  );
}
